import { useState, useEffect } from 'react';
import { emailIntegrationAPI, emailTemplatesAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle 
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, FileText, AlertCircle, Settings, Loader2 } from 'lucide-react';

/**
 * EmailCompose - Reusable email compose dialog component
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {object} contact - Contact object with email, first_name, last_name
 * @param {string} dealId - Optional deal ID to link email to
 * @param {string} ticketId - Optional ticket ID to link email to
 * @param {function} onEmailSent - Callback after email is sent successfully
 */
export const EmailCompose = ({ 
  open, 
  onOpenChange, 
  contact,
  dealId,
  ticketId,
  onEmailSent
}) => {
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    cc: '',
    bcc: ''
  });

  useEffect(() => {
    if (open) {
      checkEmailConnection();
    }
  }, [open]);

  const checkEmailConnection = async () => {
    setLoading(true);
    try {
      const [settingsRes, templatesRes] = await Promise.allSettled([
        emailIntegrationAPI.getSettings(),
        emailTemplatesAPI.getAll()
      ]);
      
      if (settingsRes.status === 'fulfilled') {
        setEmailConnected(settingsRes.value.data.connected);
      }
      if (templatesRes.status === 'fulfilled') {
        setEmailTemplates(templatesRes.value.data || []);
      }
    } catch (error) {
      setEmailConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      let body = template.body || '';
      let subject = template.subject || '';
      
      const replacements = {
        '{{first_name}}': contact?.first_name || '',
        '{{last_name}}': contact?.last_name || '',
        '{{full_name}}': `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim(),
        '{{email}}': contact?.email || '',
      };
      
      Object.entries(replacements).forEach(([key, value]) => {
        body = body.replace(new RegExp(key, 'g'), value);
        subject = subject.replace(new RegExp(key, 'g'), value);
      });
      
      setFormData(prev => ({ ...prev, subject, body }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact?.email) {
      toast.error('No email address provided');
      return;
    }

    setSending(true);
    try {
      await emailIntegrationAPI.sendEmail({
        to_email: contact.email,
        subject: formData.subject,
        body: formData.body,
        cc: formData.cc ? formData.cc.split(',').map(e => e.trim()) : [],
        bcc: formData.bcc ? formData.bcc.split(',').map(e => e.trim()) : [],
        contact_id: contact?.id,
        deal_id: dealId,
        ticket_id: ticketId
      });
      toast.success('Email sent successfully!');
      setFormData({ subject: '', body: '', cc: '', bcc: '' });
      onOpenChange(false);
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const contactName = contact 
    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email
    : 'Unknown';

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Compose Email
          </DialogTitle>
          <DialogDescription>
            Send an email to {contactName} ({contact?.email || 'No email'})
          </DialogDescription>
        </DialogHeader>

        {!emailConnected ? (
          <div className="py-6 text-center space-y-4">
            <div className="bg-amber-50 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
            <div>
              <p className="font-medium">Email Not Connected</p>
              <p className="text-sm text-muted-foreground">
                Connect your Outlook email to send emails from CRM
              </p>
            </div>
            <Button onClick={() => window.location.href = '/email-settings'}>
              <Settings className="h-4 w-4 mr-2" />
              Go to Email Settings
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Template Selection */}
            {emailTemplates.length > 0 && (
              <div className="space-y-2">
                <Label>Use Template</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="email-template-select">
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>To</Label>
              <Input value={contact?.email || ''} disabled className="bg-slate-50" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CC (comma separated)</Label>
                <Input
                  placeholder="cc@example.com"
                  value={formData.cc}
                  onChange={(e) => setFormData(p => ({ ...p, cc: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>BCC (comma separated)</Label>
                <Input
                  placeholder="bcc@example.com"
                  value={formData.bcc}
                  onChange={(e) => setFormData(p => ({ ...p, bcc: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Email subject..."
                value={formData.subject}
                onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
                required
                data-testid="compose-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Write your email..."
                value={formData.body}
                onChange={(e) => setFormData(p => ({ ...p, body: e.target.value }))}
                rows={8}
                required
                data-testid="compose-body-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending || !contact?.email}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailCompose;
